class ScShippingProgress extends HTMLElement {
  // The cart drawer is updated by morphing in server HTML, which keeps this element but
  // resets the message to the generic text and only changes data-cart-total. Re-render on those changes.
  #observer = new MutationObserver(() => this.#scheduleRender());
  #frame = 0;

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #observe() {
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-cart-total'], childList: true, characterData: true, subtree: true });
  }

  #scheduleRender() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.render());
  }

  render() {
    this.#observer.disconnect();
    try {
      this.#render();
    } finally {
      this.#observe();
    }
  }

  #render() {
    const message = this.querySelector('[data-sc-shipping-message]');
    if (!(message instanceof HTMLElement)) return;

    const exchangeRate = Number(window.Shopify?.currency?.rate || 1);
    const threshold = Math.round(Number(this.dataset.thresholdEur || 0) * exchangeRate);
    const shippingRate = Math.round(Number(this.dataset.shippingRateEur || 0) * exchangeRate);
    const cartTotal = Number(this.dataset.cartTotal || 0);
    const activeCurrency = window.Shopify?.currency?.active || this.dataset.currency || 'EUR';
    const locale = document.documentElement.lang || 'en';
    const formatMoney = (amount) => {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: activeCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(amount / 100);
      } catch (_error) {
        return `${(amount / 100).toFixed(2)} ${activeCurrency}`;
      }
    };

    const interpolate = (template, replacements) => {
      return Object.entries(replacements).reduce(
        (value, [token, replacement]) => value.replaceAll(token, replacement),
        template || ''
      );
    };

    const thresholdText = formatMoney(threshold);
    const rateText = formatMoney(shippingRate);
    const standardText = interpolate(this.dataset.standardTemplate, {
      '__rate__': rateText,
      '__threshold__': thresholdText,
      '__country__': this.dataset.country || '',
    });

    if (this.dataset.mode !== 'cart' || cartTotal <= 0 || threshold <= 0) {
      message.textContent = standardText;
      return;
    }

    const progress = Math.min(100, Math.round((cartTotal / threshold) * 100));
    const progressBar = this.querySelector('[data-sc-shipping-progress-bar]');
    const progressTrack = this.querySelector('[data-sc-shipping-progress-track]');

    if (progressBar instanceof HTMLElement) progressBar.style.width = `${progress}%`;
    if (progressTrack instanceof HTMLElement) {
      progressTrack.setAttribute('aria-valuenow', String(progress));
      progressTrack.setAttribute('aria-valuemax', '100');
    }

    if (cartTotal >= threshold) {
      message.textContent = this.dataset.successTemplate || standardText;
      this.dataset.state = 'complete';
      progressTrack?.removeAttribute('hidden');
      return;
    }

    const activationRatio = Number(this.dataset.activationRatio || 0.4);
    if (cartTotal / threshold >= activationRatio) {
      const remaining = formatMoney(threshold - cartTotal);
      message.textContent = interpolate(this.dataset.remainingTemplate, { '__amount__': remaining });
      this.dataset.state = 'near';
      progressTrack?.removeAttribute('hidden');
      return;
    }

    message.textContent = standardText;
    this.dataset.state = 'standard';
    progressTrack?.setAttribute('hidden', '');
  }
}

if (!customElements.get('sc-shipping-progress')) {
  customElements.define('sc-shipping-progress', ScShippingProgress);
}
